import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpBubble } from '@/components/HelpBubble';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Shield, Database, AlertTriangle, CheckCircle, XCircle,
  Clock, Activity, Brain, RefreshCw, Lock, FileCheck, Users,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const TIER_CONFIG = {
  ai_ready: { label: 'AI-Ready', color: 'text-emerald-600', bg: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  usable_with_caution: { label: 'Usable (Caution)', color: 'text-amber-600', bg: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  requires_cleansing: { label: 'Requires Cleansing', color: 'text-red-500', bg: 'bg-red-100 text-red-800', icon: XCircle },
  no_data: { label: 'No Data', color: 'text-muted-foreground', bg: 'bg-muted text-muted-foreground', icon: Database },
};

const FRESHNESS_CONFIG = {
  fresh: { label: 'Fresh', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  aging: { label: 'Aging', color: 'text-amber-600', bg: 'bg-amber-50' },
  stale: { label: 'Stale', color: 'text-red-500', bg: 'bg-red-50' },
};

function HealthGauge({ score }) {
  const radius = 60;
  const stroke = 12;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative flex items-center justify-center" data-testid="governance-health-gauge">
      <svg width="150" height="150" viewBox="0 0 150 150">
        <circle cx="75" cy="75" r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth={stroke} />
        <circle cx="75" cy="75" r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform="rotate(-90 75 75)" style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div className="absolute text-center">
        <p className="text-3xl font-bold font-mono" style={{ color }} data-testid="governance-health-value">{score}</p>
        <p className="text-[10px] text-muted-foreground font-medium">HEALTH</p>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, title, value, subtitle, status, help, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-muted shrink-0">
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
              {title}
              {help ? <HelpBubble text={help} /> : null}
            </p>
            <p className="text-lg font-bold font-mono mt-0.5">{value}</p>
            {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>}
            {status && <Badge className={`mt-1 text-[10px] ${status.bg}`}>{status.label}</Badge>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PolicyCard({ title, status, details }) {
  const isCompliant = status === 'compliant';
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${isCompliant ? 'bg-emerald-50/50 border-emerald-200' : 'bg-red-50/50 border-red-200'}`}
      data-testid={`policy-${title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      {isCompliant
        ? <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
        : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-[10px] text-muted-foreground">{details}</p>
      </div>
      <Badge className={`text-[10px] shrink-0 ${isCompliant ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
        {isCompliant ? 'Compliant' : 'Gap'}
      </Badge>
    </div>
  );
}

export default function GovernanceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/governance/dashboard');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load governance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="governance-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const tierCfg = TIER_CONFIG[data.pds?.tier] || TIER_CONFIG.no_data;
  const TierIcon = tierCfg.icon;
  const freshCfg = FRESHNESS_CONFIG[data.data_freshness?.status] || FRESHNESS_CONFIG.stale;

  const consentGranted = Object.values(data.consent_status || {}).filter(v => v?.granted).length;
  const consentTotal = 3;

  // Derive policy compliance
  const policies = [
    {
      title: 'No data enters without validation',
      status: 'compliant',
      details: 'Upload pipeline enforces required field checks and format validation',
    },
    {
      title: 'No AI runs on unscored data',
      status: data.pds?.tier === 'requires_cleansing' || data.pds?.tier === 'no_data' ? 'compliant' : 'compliant',
      details: `PDS gate active — AI blocked below 40%. Current score: ${data.pds?.score}%`,
    },
    {
      title: 'All insights must be explainable',
      status: 'compliant',
      details: 'AI anonymization + audit trail logs every AI call with data lineage',
    },
    {
      title: 'Data ownership always defined',
      status: 'compliant',
      details: 'Every record tagged with organization_id, all actions tracked in audit log',
    },
    {
      title: 'Data retention enforced',
      status: data.retention?.purge_scheduler_active ? 'compliant' : 'gap',
      details: `${data.retention?.policy_years}-year retention policy with automated purge scheduler`,
    },
    {
      title: 'GDPR consent management',
      status: consentGranted >= 2 ? 'compliant' : 'gap',
      details: `${consentGranted}/${consentTotal} consents granted`,
    },
    {
      title: 'Multi-factor authentication',
      status: data.security?.mfa_enabled ? 'compliant' : 'gap',
      details: data.security?.mfa_enabled
        ? `MFA active: ${data.security.mfa_enabled_count}/${data.security.total_users} users (${data.security.mfa_pct}%)`
        : `${data.security?.mfa_enabled_count || 0}/${data.security?.total_users || 0} users have MFA enabled — enable via Settings > Account`,
    },
    {
      title: 'Supplier KYC verification',
      status: (data.supplier_verification?.verified_pct || 0) >= 50 ? 'compliant' : 'gap',
      details: `${data.supplier_verification?.verified_pct || 0}% of suppliers verified (${data.supplier_verification?.verified || 0}/${data.supplier_verification?.total_suppliers || 0})`,
    },
  ];

  const compliantCount = policies.filter(p => p.status === 'compliant').length;

  return (
    <div className="space-y-6 p-6" data-testid="governance-dashboard">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Data Governance</h1>
          <p className="text-sm text-muted-foreground mt-1">Framework compliance, data quality enforcement, and operational KPIs</p>
        </div>
        <button onClick={fetchData} className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="governance-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </motion.div>

      {/* Top Row: Health Gauge + PDS + Key KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:row-span-2" data-testid="governance-health-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="w-4 h-4" /> Governance Health
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-3">
            <HealthGauge score={data.governance_health} />
            <div className="text-center">
              <Badge className={`${tierCfg.bg} text-xs`}>
                <TierIcon className="w-3 h-3 mr-1" />
                PDS: {data.pds?.score}% — {tierCfg.label}
              </Badge>
            </div>
            <div className="w-full space-y-2 mt-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Policy Compliance</span>
                <span className="font-mono">{compliantCount}/{policies.length}</span>
              </div>
              <Progress value={(compliantCount / policies.length) * 100} className="h-1.5" />
            </div>
          </CardContent>
        </Card>

        <KpiCard
          icon={Database}
          title="Data Quality Score (PDS)"
          value={`${data.pds?.score}%`}
          subtitle={`${data.pds?.total_records?.toLocaleString()} records scored`}
          status={{ label: tierCfg.label, bg: tierCfg.bg }}
          help="Composite data quality score from field completeness, classification coverage, and supplier normalization. 70%+ unlocks full AI capability."
          testId="kpi-pds"
        />
        <KpiCard
          icon={Users}
          title="Duplicate Supplier Rate"
          value={`${data.duplicate_suppliers?.duplicate_rate_pct}%`}
          subtitle={`${data.duplicate_suppliers?.duplicates_detected} duplicates in ${data.duplicate_suppliers?.total_suppliers} suppliers`}
          status={{
            label: data.duplicate_suppliers?.duplicate_rate_pct < 5 ? 'Low' : data.duplicate_suppliers?.duplicate_rate_pct < 15 ? 'Moderate' : 'High',
            bg: data.duplicate_suppliers?.duplicate_rate_pct < 5 ? 'bg-emerald-100 text-emerald-800' : data.duplicate_suppliers?.duplicate_rate_pct < 15 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
          }}
          help="Share of suppliers detected as near-duplicates by name normalization. Lower is better — merge candidates in Supplier Directory."
          testId="kpi-duplicates"
        />
        <KpiCard
          icon={FileCheck}
          title="Classification Coverage"
          value={`${data.classification?.coverage_pct}%`}
          subtitle={`${data.classification?.classified?.toLocaleString()} of ${data.classification?.total_transactions?.toLocaleString()} classified`}
          help="Share of transactions with a Level 1 category assigned. Low coverage caps the usefulness of Category Console and Spend Analysis."
          testId="kpi-classification"
        />
        <KpiCard
          icon={Clock}
          title="Data Freshness"
          value={data.data_freshness?.days_since_last_upload != null ? `${data.data_freshness.days_since_last_upload}d` : 'N/A'}
          subtitle="Days since last upload"
          status={{ label: freshCfg.label, bg: `${freshCfg.bg} ${freshCfg.color}` }}
          help="Days since the most recent CSV upload. Fresh = under 7 days, Aging = under 30, Stale = over 30 days."
          testId="kpi-freshness"
        />
        <KpiCard
          icon={Activity}
          title="Audit Events (30d)"
          value={data.audit_activity?.events_last_30d?.toLocaleString()}
          subtitle={`Last: ${data.audit_activity?.last_event?.action || 'N/A'}`}
          help="Count of auditable actions (logins, uploads, admin changes, AI calls) in the last 30 days — a proxy for platform engagement."
          testId="kpi-audit"
        />
        <KpiCard
          icon={Brain}
          title="AI Calls (30d)"
          value={data.audit_activity?.ai_calls_last_30d?.toLocaleString()}
          subtitle="PDS-gated AI executions"
          help="LLM-backed AI executions in the last 30 days. Each call is PDS-gated and quota-counted — contributes to the daily cost circuit breaker."
          testId="kpi-ai-calls"
        />
        {data.supplier_verification && (
          <KpiCard
            icon={Shield}
            title="Supplier KYC Rate"
            value={`${data.supplier_verification.verified_pct}%`}
            subtitle={`${data.supplier_verification.verified} verified of ${data.supplier_verification.total_suppliers}`}
            status={{
              label: data.supplier_verification.verified_pct >= 50 ? 'On Track' : 'Below Target',
              bg: data.supplier_verification.verified_pct >= 50 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
            }}
            help="Share of suppliers with a verified KYC record. Verifications auto-expire after 12 months and must be refreshed."
            testId="kpi-supplier-kyc"
          />
        )}
      </div>

      {/* PDS Section Breakdown */}
      <Card data-testid="pds-breakdown">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Database className="w-4 h-4" /> PDS Section Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(data.pds?.sections || {}).map(([name, score]) => {
              const color = score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-red-500';
              const barColor = score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-400';
              return (
                <div key={name} className="flex items-center gap-3 p-2 rounded-lg border bg-card">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.max(score, 2)}%`, transition: 'width 0.6s ease' }} />
                      </div>
                      <span className={`text-xs font-mono ${color}`}>{score}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Policy Compliance */}
      <Card data-testid="policy-compliance">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Lock className="w-4 h-4" /> Non-Negotiable Policy Compliance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {policies.map((p, i) => (
              <PolicyCard key={i} {...p} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Retention & Security */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="retention-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" /> Data Retention
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Policy</span>
              <span className="font-mono font-medium">{data.retention?.policy_years} years</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Auto-Purge Scheduler</span>
              <Badge className={data.retention?.purge_scheduler_active ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                {data.retention?.purge_scheduler_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Oldest Record</span>
              <span className="font-mono text-xs">{data.retention?.oldest_record_date || 'N/A'}</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="security-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="w-4 h-4" /> Security Posture
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">MFA</span>
              <Badge className={data.security?.mfa_enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                {data.security?.mfa_enabled ? 'Enabled' : 'Planned'}
              </Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">RBAC</span>
              <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Locked Accounts</span>
              <span className="font-mono font-medium">{data.security?.locked_accounts || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Encryption in Transit</span>
              <Badge className="bg-emerald-100 text-emerald-800">TLS / HSTS</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
